import React, { useEffect, useState, useMemo } from 'react';
import { Autocomplete, TextField, CircularProgress } from '@mui/material';
import { useDataProvider, useNotify, RaRecord, Identifier } from 'react-admin';
import { TPADataType, TpaListProps } from '../../utils/DataTypes';
import { FORM_LABELS } from '../../constants';

const TpaList = ({ setFunction, reset, value }: TpaListProps) => {
  const dataProvider = useDataProvider();
  const notify = useNotify();
  const [tpaList, setTpaList] = useState<TPADataType[]>([]);
  const [loading, setLoading] = useState(true);
  const [key, setKey] = useState(0);
  const [inputValue, setInputValue] = useState('');

  const filteredOptions = useMemo(
    () =>
      tpaList.filter(
        (option) =>
          (option.vendor_name && option.vendor_name.toLowerCase().includes(inputValue.toLowerCase())) ||
          (option.vendor_number && String(option.vendor_number).includes(inputValue.toLowerCase()))
      ),
    [tpaList, inputValue]
  );

  useEffect(() => {
    setLoading(true);
    dataProvider
      .getList('Tpa', {
        pagination: { page: 1, perPage: 1000 },
        sort: { field: 'vendor_name', order: 'ASC' },
        filter: {},
      })
      .then(({ data }) => {
        setTpaList(data);
        setLoading(false);
      })
      .catch((error) => {
        notify('Error: Could not fetch TPA list', { type: 'error' });
        setLoading(false);
      });
  }, [dataProvider, notify]);

  useEffect(() => {
    if (reset) {
      setKey((prevKey) => prevKey + 1);
    }
  }, [reset]);

  return (
    <Autocomplete
      key={key}
      value={value}
      multiple
      limitTags={2}
      options={filteredOptions}
      loading={loading}
      disabled={loading}
      filterSelectedOptions
      disableCloseOnSelect
      getOptionLabel={(option) => {
        if (option && option.vendor_name) {
          return `${option.vendor_name} (${option.vendor_number})`;
        }
        return '';
      }}
      isOptionEqualToValue={(option, value) => option.vendor_number === value.vendor_number}
      onChange={(event, newValue) => setFunction(newValue)}
      inputValue={inputValue}
      onInputChange={(event, newInputValue) => {
        setInputValue(newInputValue || '');
      }}
      filterOptions={(x) => x}
      renderOption={(props, option) => (
        <li {...props} key={option.vendor_number}>
          {`${option.vendor_name} (${option.vendor_number})`}
        </li>
      )}
      renderInput={(params) => (
        <TextField
          {...params}
          label={FORM_LABELS.TPAS}
          placeholder="Search by name or number..."
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default TpaList;
